/**
 * Composes a formPath and parentPath while avoiding path duplication.
 * This is useful when you need to build a full parent path that accounts
 * for the form's current position in the schema hierarchy.
 *
 * @param formPath - The normalized form path (empty string if at root)
 * @param parentPath - The parent path relative to the current field
 * @returns The composed parent path without duplicated segments
 *
 * @example
 * // formPath='image', parentPath='image' → 'image' (avoids 'image.image')
 * // formPath='image', parentPath='image.nested' → 'image.nested' (avoids 'image.image.nested')
 * // formPath='image', parentPath='other' → 'image.other' (composes correctly)
 * // formPath='', parentPath='nested' → 'nested' (no formPath to compose)
 */
export const composeParentPath = (
  formPath: string,
  parentPath: string,
): string => {
  // Case 1: No formPath - use parentPath as-is
  if (!formPath) return parentPath;
  // Case 2: No parentPath - formPath is the parent
  if (!parentPath) return formPath;
  // Case 3: parentPath equals formPath - avoid duplication
  if (parentPath === formPath) return parentPath;
  // Case 4: parentPath already includes formPath - use as-is
  if (parentPath.startsWith(`${formPath}.`)) return parentPath;
  // Case 5: Compose formPath and parentPath
  return `${formPath}.${parentPath}`;
};
